import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Upload, AlertCircle, CheckCircle2 } from 'lucide-react';
import { T, ROLES, springs } from '../../lib/tokens';
import { Glass } from '../glass/Glass';
import { apiFetch, isLocalAvailable } from '../../lib/api';

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '10px 14px', borderRadius: 10, fontSize: 13,
  border: '1px solid rgba(0,0,0,0.10)', background: 'rgba(255,255,255,0.85)',
  fontFamily: 'inherit', color: T.text, outline: 'none',
};

const VENTURE_OPTIONS = [
  { slug: 'oneday',   label: 'Oneday (mentor 1:1)' },
  { slug: 'liken',    label: 'Liken' },
  { slug: 'pryke',    label: 'Pryke' },
  { slug: 'personal', label: 'Personal' },
];

export const ImportMeetingDialog = ({ founders = [], onClose, onImported }) => {
  const [url, setUrl] = useState('');
  const [roleSlug, setRoleSlug] = useState('liken');
  const [founderId, setFounderId] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Reset founder selection when role changes away from Oneday
  useEffect(() => { if (roleSlug !== 'oneday') setFounderId(''); }, [roleSlug]);

  const valid = url.trim() && roleSlug && (roleSlug !== 'oneday' || founders.length === 0 || founderId);

  const submit = async (e) => {
    e?.preventDefault();
    if (!valid || busy || !isLocalAvailable()) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await apiFetch('/meetings/import-link', {
        method: 'POST',
        body: JSON.stringify({
          fireflies_url: url.trim(),
          role_slug: roleSlug,
          founder_id: roleSlug === 'oneday' && founderId ? founderId : undefined,
          title: title.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || data.error || `Import failed (${res.status})`);
      }
      setSuccess(
        data.actions != null
          ? `Imported and processed (${data.actions} action${data.actions === 1 ? '' : 's'} extracted)`
          : 'Imported. Processing in background.'
      );
      if (onImported) onImported();
      // Auto-close after short delay so the user sees the success state
      setTimeout(() => { onClose(); }, 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: 100 }}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={springs.gentle}
        style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 480, maxWidth: '90vw', zIndex: 101 }}
      >
        <Glass depth={3} hover={false} style={{ padding: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 30, height: 30, borderRadius: 8,
                background: T.violetLight, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Upload size={15} color={T.violet} />
              </div>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: 0 }}>Import Fireflies Meeting</h3>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                  Paste the link, pick the venture, get the action items.
                </div>
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <X size={16} color={T.textMuted} />
            </button>
          </div>

          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                Fireflies link
              </div>
              <input
                ref={inputRef}
                type="url"
                value={url}
                onChange={e => setUrl(e.target.value)}
                placeholder="https://app.fireflies.ai/view/..."
                disabled={busy}
                style={inputStyle}
              />
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                Venture
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                {VENTURE_OPTIONS.map(v => {
                  const role = ROLES[v.slug];
                  const selected = roleSlug === v.slug;
                  return (
                    <button
                      key={v.slug}
                      type="button"
                      onClick={() => setRoleSlug(v.slug)}
                      disabled={busy}
                      style={{
                        padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                        cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit',
                        border: selected ? `1.5px solid ${role?.color || T.amber}` : '1px solid rgba(0,0,0,0.08)',
                        background: selected ? (role?.light || T.amberLight) : 'rgba(255,255,255,0.6)',
                        color: selected ? (role?.color || T.amber) : T.textMid,
                        textAlign: 'left',
                      }}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {roleSlug === 'oneday' && founders.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                  Founder <span style={{ color: T.red }}>*</span>
                </div>
                <select value={founderId} onChange={e => setFounderId(e.target.value)} style={inputStyle} disabled={busy}>
                  <option value="">— Select founder —</option>
                  {founders.map(f => (
                    <option key={f.id} value={f.id}>{f.name}{f.company_name ? ` · ${f.company_name}` : ''}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                Title <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional, defaults to Fireflies title)</span>
              </div>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Liken Weekly Founders Meet"
                disabled={busy}
                style={inputStyle}
              />
            </div>

            {error && (
              <div style={{ fontSize: 12, color: T.red, display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', background: T.redLight, borderRadius: 8 }}>
                <AlertCircle size={13} /> {error}
              </div>
            )}
            {success && (
              <div style={{ fontSize: 12, color: T.green, display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', background: T.greenLight, borderRadius: 8 }}>
                <CheckCircle2 size={13} /> {success}
              </div>
            )}

            <button
              type="submit"
              disabled={!valid || busy || !isLocalAvailable()}
              style={{
                padding: '10px 20px', borderRadius: 10, border: 'none',
                background: T.amber, color: '#fff',
                cursor: (!valid || busy || !isLocalAvailable()) ? 'default' : 'pointer',
                fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                opacity: (!valid || busy || !isLocalAvailable()) ? 0.5 : 1,
                marginTop: 4,
              }}
            >
              {busy ? 'Importing…' : 'Import & process'}
            </button>
            {!isLocalAvailable() && (
              <div style={{ fontSize: 11, color: T.textMuted, textAlign: 'center' }}>
                Local server required.
              </div>
            )}
          </form>
        </Glass>
      </motion.div>
    </>
  );
};
